import { useId } from 'react';
import { Avatar, AvatarFallback, Badge, Skeleton } from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { trackerHueClasses, trackerIconComponent } from '@/entities/tracker';
import { type TemplateCard, type TrackerTemplateVisibility } from '@/entities/tracker-template';
import { useUserDisplay } from '@/entities/user';

// Keyed off the same strings `TEMPLATE_VISIBILITIES` renders, so the card badge and the
// visibility picker cannot drift apart.
const VISIBILITY_LABEL_KEY = {
  private: 'trackerTemplate.types.private',
  workspace: 'trackerTemplate.types.workspace',
  organization: 'trackerTemplate.types.organization',
} as const satisfies Record<TrackerTemplateVisibility, string>;

function CreatorFooter({ userId, id }: { userId: string; id: string }) {
  const { t } = useTranslation('trackers');
  const creator = useUserDisplay(userId);

  return (
    <span
      id={id}
      className="flex items-center gap-xs border-t border-border-light px-lg py-sm type-caption text-muted-foreground"
    >
      <Avatar aria-hidden="true" className="size-6 shrink-0">
        <AvatarFallback className="bg-accent text-label-sm font-medium text-accent-foreground uppercase">
          {creator.initials ?? '?'}
        </AvatarFallback>
      </Avatar>
      {creator.isLoading ? (
        <Skeleton className="h-4 w-24" data-testid="template-card-creator-pending" />
      ) : creator.name ? (
        t('trackers.trackersHubEmptyState.createdBy', { name: creator.name })
      ) : (
        t('trackers.trackersHubEmptyState.createdByTeammate')
      )}
    </span>
  );
}

export function TemplateCoverCard({
  card,
  onPick,
  disabled,
}: {
  card: TemplateCard;
  onPick: (card: TemplateCard) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation('trackers');
  const uid = useId();
  const titleId = `${uid}-title`;
  const badgeId = `${uid}-badge`;
  const descriptionId = `${uid}-description`;
  const footerId = `${uid}-footer`;
  const isBuiltin = card.source === 'builtin';
  const name = isBuiltin ? card.builtin.displayName : card.template.name;
  const description = isBuiltin ? card.builtin.description : card.template.description;
  const { icon, hue } = isBuiltin ? card.builtin : card.template;
  const Icon = trackerIconComponent(icon);
  const { tile, foreground } = trackerHueClasses(hue);
  const badge = isBuiltin
    ? t('trackers.templateGalleryDialog.default')
    : t(VISIBILITY_LABEL_KEY[card.template.visibility]);

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onPick(card)}
      aria-labelledby={titleId}
      aria-describedby={[badgeId, descriptionId, !isBuiltin && footerId].filter(Boolean).join(' ')}
      className="flex flex-col overflow-hidden rounded-lg border border-border-light bg-card text-left transition-colors hover:border-border focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-60"
    >
      <span className={`relative flex h-30 items-center justify-center ${tile}`}>
        <Icon className={`size-8 ${foreground}`} strokeWidth={1.75} aria-hidden="true" />
        {/* Multiply lets the cover's hue show through the badge's grey. On a
            dark cover it would sink the badge to black, so dark mode drops it. */}
        <span className="absolute top-sm right-sm flex mix-blend-multiply dark:mix-blend-normal">
          <Badge id={badgeId} variant="secondary">
            {badge}
          </Badge>
        </span>
      </span>
      <span className="flex flex-1 flex-col gap-2xs p-lg">
        <span id={titleId} className="type-heading-sm text-foreground">
          {name}
        </span>
        <span
          id={descriptionId}
          className="line-clamp-3 type-body-md text-pretty text-muted-foreground"
        >
          {description ?? t('trackers.customTracker')}
        </span>
      </span>
      {!isBuiltin && <CreatorFooter id={footerId} userId={card.template.createdBy} />}
    </button>
  );
}
